import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Alert } from '@/components/ui/Alert'
import { Skeleton } from '@/components/ui/Skeleton'
import { UserAvatar } from '@/features/social/components/UserAvatar'
import { useDailyGame } from '../hooks/useDailyGame'
import { useDailyLeaderboard } from '../hooks/useDailyLeaderboard'

/**
 * Le jeu du jour, en évidence (SPEC §9, §12, Lot J).
 *
 * Trois états mutuellement exclusifs. Deux reflètent `DailyGameDto.already_played` :
 * pas encore joué (CTA vers la partie), ou déjà joué — dans ce cas le
 * résultat et le classement du jour remplacent le bouton, qui serait sinon
 * mort (`POST /practice/runs` refuserait une deuxième partie `daily` du même
 * jour avec `DAILY_ALREADY_PLAYED`). Le troisième — `daily === null` — reflète
 * `NO_DAILY_GAME_AVAILABLE` (`useDailyGame`) : aucune discipline active ne
 * publie encore d'items, un état produit neutre, pas une panne. Seule une
 * vraie erreur technique (`isError`) déclenche l'alerte.
 */
export function DailyCard() {
  const { t } = useTranslation()
  const { data: daily, isLoading, isError } = useDailyGame()
  const alreadyPlayed = daily?.already_played === true
  const isUnavailable = !isLoading && !isError && daily === null
  const { data: leaderboard, isLoading: isLeaderboardLoading } = useDailyLeaderboard(alreadyPlayed)

  return (
    <section
      aria-labelledby="practice-daily-title"
      className="flex flex-col gap-4 rounded-3xl bg-primary/5 p-5 md:p-6"
    >
      <h2 id="practice-daily-title" className="font-display text-lg font-semibold text-on-surface">
        {t('practice.daily.title')}
      </h2>

      {isError && <Alert variant="error">{t('practice.daily.load_error')}</Alert>}

      {isUnavailable && (
        <p className="text-sm text-on-surface-variant">{t('practice.daily.unavailable')}</p>
      )}

      {isLoading && (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-6 w-2/3" />
          <Skeleton className="h-11 w-40" />
        </div>
      )}

      {daily && !alreadyPlayed && (
        <div className="flex flex-col items-start gap-3">
          <p className="text-sm text-on-surface-variant">
            {t('practice.daily.subtitle', { game: t(`practice.games.${daily.game}.name`) })}
          </p>
          <Link
            to={`/jeux/${daily.game}?mode=daily`}
            className="inline-flex min-h-[44px] items-center rounded-full bg-primary px-5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
          >
            {t('practice.daily.play')}
          </Link>
        </div>
      )}

      {daily && alreadyPlayed && daily.result && (
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-3 gap-2">
            <div className="flex flex-col items-center gap-0.5 rounded-2xl bg-surface-low p-3 text-center">
              <span className="font-display text-lg font-bold tabular-nums text-primary">
                {t('practice.result.score_value', { score: daily.result.score, maxScore: daily.result.max_score })}
              </span>
              <span className="text-[11px] text-on-surface-variant">{t('practice.result.score_label')}</span>
            </div>
            <div className="flex flex-col items-center gap-0.5 rounded-2xl bg-surface-low p-3 text-center">
              <span className="font-display text-lg font-bold tabular-nums text-secondary">
                {t('practice.result.accuracy_value', { accuracy: daily.result.accuracy })}
              </span>
              <span className="text-[11px] text-on-surface-variant">{t('practice.result.accuracy_label')}</span>
            </div>
            {daily.result.pattern && (
              <div className="flex flex-col items-center justify-center gap-0.5 rounded-2xl bg-surface-low p-3 text-center">
                <span aria-hidden="true" className="text-sm tracking-wide">
                  {daily.result.pattern}
                </span>
                <span className="text-[11px] text-on-surface-variant">{t('practice.daily.pattern_label')}</span>
              </div>
            )}
          </div>

          <p className="text-sm font-medium text-success">{t('practice.daily.already_played')}</p>

          <div className="flex flex-col gap-2">
            <h3 className="text-sm font-semibold text-on-surface">{t('practice.daily.leaderboard_title')}</h3>

            {isLeaderboardLoading && (
              <div className="flex flex-col gap-2">
                {['sk-1', 'sk-2', 'sk-3'].map((id) => (
                  <Skeleton key={id} className="h-12" />
                ))}
              </div>
            )}

            {leaderboard && leaderboard.entries.length === 0 && (
              <p className="text-sm text-on-surface-variant">{t('practice.daily.leaderboard_empty')}</p>
            )}

            {leaderboard && leaderboard.entries.length > 0 && (
              <ul className="flex flex-col gap-2">
                {leaderboard.entries.map((entry) => (
                  <li
                    key={entry.user_id}
                    className={[
                      'flex items-center gap-3 rounded-2xl px-3 py-2',
                      entry.is_me ? 'border border-primary bg-primary/5' : 'bg-surface-low',
                    ].join(' ')}
                  >
                    <span className="w-6 shrink-0 text-center font-display text-sm font-bold tabular-nums text-on-surface-variant">
                      {entry.rank}
                    </span>
                    <UserAvatar username={entry.username} avatarUrl={entry.avatar_url} size="sm" />
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-on-surface">
                      {entry.username ?? t('practice.daily.anonymous')}
                    </span>
                    <span className="font-display text-sm font-bold tabular-nums text-secondary">
                      {entry.score}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </section>
  )
}
